import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Custom hook for adding retry functionality to React Query queries
 * Provides manual retry with exponential backoff and retry limits
 */
export function useRetryableQuery(queryKey, options = {}) {
  const queryClient = useQueryClient();
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  
  const {
    maxRetries = 3,
    baseDelay = 1000,
    maxDelay = 10000,
    onRetrySuccess,
    onRetryFailure,
    onMaxRetriesReached,
  } = options;

  const calculateDelay = useCallback((attempt) => {
    // Exponential backoff with jitter
    const exponentialDelay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
    const jitter = Math.random() * 0.1 * exponentialDelay;
    return exponentialDelay + jitter;
  }, [baseDelay, maxDelay]);

  const retry = useCallback(async () => {
    if (retryCount >= maxRetries) {
      console.warn(`Maximum retry attempts (${maxRetries}) reached for query:`, queryKey);
      onMaxRetriesReached?.();
      return false;
    }

    setIsRetrying(true);
    const currentAttempt = retryCount + 1;
    
    try {
      // Calculate delay for this attempt
      const delay = calculateDelay(currentAttempt - 1);
      
      console.log(`Retrying query (attempt ${currentAttempt}/${maxRetries}) after ${Math.round(delay)}ms:`, queryKey);
      
      // Wait for the calculated delay
      await new Promise(resolve => setTimeout(resolve, delay));
      
      // Invalidate and refetch the query
      await queryClient.invalidateQueries({ queryKey });
      await queryClient.refetchQueries({ queryKey });
      
      setRetryCount(currentAttempt);
      setIsRetrying(false);
      
      console.log(`Retry attempt ${currentAttempt} completed for query:`, queryKey);
      onRetrySuccess?.(currentAttempt);
      
      return true;
    } catch (error) {
      console.error(`Retry attempt ${currentAttempt} failed for query:`, queryKey, error);
      setRetryCount(currentAttempt);
      setIsRetrying(false);
      
      onRetryFailure?.(error, currentAttempt);
      
      // If this was the last attempt, call the max retries callback
      if (currentAttempt >= maxRetries) {
        onMaxRetriesReached?.();
      }
      
      return false;
    }
  }, [
    queryKey,
    retryCount,
    maxRetries,
    calculateDelay,
    queryClient,
    onRetrySuccess,
    onRetryFailure,
    onMaxRetriesReached,
  ]);

  const reset = useCallback(() => {
    setRetryCount(0);
    setIsRetrying(false);
  }, []);

  const canRetry = retryCount < maxRetries;

  return {
    retry,
    reset,
    retryCount,
    isRetrying,
    canRetry,
    maxRetries,
  };
}

export default useRetryableQuery;